// Central media-audio engine for BuzzSpell.
//
// Why HTMLAudioElement (not Web Audio API):
// On iPhone, audio routed through `AudioContext` is treated as *ambient*
// sound and gets MUTED by the hardware Silent / Ring switch. Apps like
// YouTube don't mute in Silent Mode because they play through
// `<audio>` / HTMLMediaElement, which iOS routes through the **media**
// audio session.
//
// This module wraps two HTMLAudioElement instances:
//   - `main`: word / letter / TTS playback (sequential, interruptible)
//   - `chime`: success/fail feedback dings (overlay; doesn't disturb main)
// plus a one-time "unlock" routine that plays a silent MP3 inside the
// first user gesture so subsequent `.play()` calls are allowed and play
// through the media channel (bypassing Silent Mode).
//
// Public API (see exports below):
//   unlockAudio()                  -> Promise<boolean>   idempotent
//   isAudioUnlocked()              -> boolean
//   playUrl(url)                   -> Promise<void>      main, interruptible
//   playB64(b64, mime?)            -> Promise<void>
//   preloadB64(key, b64, mime?)    -> string|null        cached blob URL
//   clearCachedAudio(prefix)       -> void
//   stop()                         -> void               interrupts main
//   pause()/resume()               -> void
//   playChime(url)                 -> Promise<void>      overlay
//
// Notes:
// - All callers go through this module so the iOS unlock state is
//   shared. NEVER `new Audio()` outside of this file for word/chime
//   playback.
// - Blob URLs are page-scoped: they live for the page's lifetime so we
//   don't have to re-fetch + re-decode bytes for the same word.

const isIOS = /iPad|iPhone|iPod/.test(typeof navigator !== "undefined" ? navigator.userAgent : "")
  || (typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

let _unlocked = false;
let _main = null;
let _chime = null;
let _mainCurrentResolve = null;
let _mainCurrentPromise = null;

function _newElement() {
  const el = new Audio();
  el.setAttribute("playsinline", "true");
  el.setAttribute("webkit-playsinline", "true");
  el.preload = "auto";
  return el;
}

function getMain() {
  if (_main) return _main;
  _main = _newElement();
  if (typeof window !== "undefined") window.__buzzAudioMain = _main;
  return _main;
}

function getChime() {
  if (_chime) return _chime;
  _chime = _newElement();
  if (typeof window !== "undefined") window.__buzzAudioChime = _chime;
  return _chime;
}

/**
 * Stop main playback immediately and resolve its awaiting promise so
 * the run loop can move on without waiting for `ended`.
 */
function _resetMain() {
  const el = _main;
  if (!el) return;
  try {
    el.pause();
    el.currentTime = 0;
    // Releasing the src lets the browser garbage-collect the previous
    // network resource and avoids "interrupted" errors firing AFTER we
    // start the next src.
    el.removeAttribute("src");
    el.load();
  } catch { /* ignore */ }
  const r = _mainCurrentResolve;
  _mainCurrentResolve = null;
  _mainCurrentPromise = null;
  try { r?.(); } catch { /* ignore */ }
}

/**
 * One-time iOS unlock. Plays a 0.2s silent MP3 inside the calling user
 * gesture so the HTMLAudioElements are "approved" by Safari for the
 * rest of the page lifetime. Safe to call repeatedly — only the first
 * call does real work.
 *
 * Returns true if unlock succeeded (or wasn't needed); false on failure.
 */
export async function unlockAudio() {
  if (_unlocked) return true;
  const m = getMain();
  const c = getChime();
  // Unlock BOTH elements in the same gesture — iOS tracks the
  // "user-activation -> media element allowed" bit per element.
  const tryUnlock = async (el) => {
    try {
      el.src = "/silent.mp3";
      el.muted = true;
      el.volume = 0;
      await el.play();
      el.pause();
      el.currentTime = 0;
      el.muted = false;
      el.volume = 1;
      return true;
    } catch (err) {
      console.warn("[audio] unlock element failed:", err?.name || err);
      return false;
    }
  };
  const okMain = await tryUnlock(m);
  const okChime = await tryUnlock(c);
  _unlocked = okMain && okChime;
  if (_unlocked) {
    console.debug("[audio] unlocked (iOS-compatible HTMLAudioElement)");
  }
  return _unlocked;
}

export const isAudioUnlocked = () => _unlocked;

/**
 * Convert a base64 (no data: prefix) string + mime type into a Blob URL.
 */
function b64ToBlobUrl(b64, mime = "audio/mpeg") {
  try {
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    return URL.createObjectURL(blob);
  } catch (err) {
    console.warn("[audio] b64 → blob failed", err);
    return null;
  }
}

// Page-scoped cache so repeated playWord calls reuse the same Blob URL
// (one resource per unique audio payload — keeps memory bounded).
const _blobUrlCache = new Map(); // key -> blobUrl

/**
 * Preload a base64-encoded audio payload, returning the Blob URL.
 * Cached by `key` so subsequent calls return instantly.
 */
export function preloadB64(key, b64, mime = "audio/mpeg") {
  if (!key) return null;
  const hit = _blobUrlCache.get(key);
  if (hit) return hit;
  if (!b64) return null;
  const url = b64ToBlobUrl(b64, mime);
  if (url) _blobUrlCache.set(key, url);
  return url;
}

/**
 * Drop entries from the in-memory Blob URL cache whose keys start with
 * the given prefix. Admins call this after regenerating audio for a
 * word so the next playback fetches fresh bytes from the backend.
 */
export function clearCachedAudio(keyPrefix) {
  if (!keyPrefix) return;
  for (const k of Array.from(_blobUrlCache.keys())) {
    if (k.startsWith(keyPrefix)) {
      try { URL.revokeObjectURL(_blobUrlCache.get(k)); } catch { /* ignore */ }
      _blobUrlCache.delete(k);
    }
  }
}

/**
 * Play an audio URL through the main element. Resolves when playback
 * ends (or is interrupted via stop()). Stops any previous main playback
 * first so callers can safely `await` sequentially.
 */
export function playUrl(url) {
  if (!url) return Promise.resolve();
  // Interrupt previous playback. The previous awaiter resolves cleanly.
  _resetMain();
  const el = getMain();
  const p = new Promise((resolve, reject) => {
    _mainCurrentResolve = resolve;
    const cleanup = () => {
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onErr);
    };
    const onEnd = () => {
      cleanup();
      if (_mainCurrentResolve === resolve) {
        _mainCurrentResolve = null;
        _mainCurrentPromise = null;
      }
      resolve();
    };
    const onErr = (e) => {
      cleanup();
      if (_mainCurrentResolve === resolve) {
        _mainCurrentResolve = null;
        _mainCurrentPromise = null;
      }
      console.warn("[audio] play error:", e?.error || e?.message || e?.name || e);
      reject(e?.error || new Error("audio play error"));
    };
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onErr);
    try {
      el.src = url;
      const pp = el.play();
      if (pp && typeof pp.catch === "function") {
        pp.catch(async (err) => {
          // NotAllowedError on iOS happens if unlock didn't run yet.
          if (!_unlocked) {
            const ok = await unlockAudio();
            if (!ok) return onErr(err);
            try {
              el.src = url;
              const pp2 = el.play();
              if (pp2 && typeof pp2.catch === "function") pp2.catch(onErr);
              return;
            } catch (e) { return onErr(e); }
          }
          onErr(err);
        });
      }
    } catch (e) {
      onErr(e);
    }
  });
  _mainCurrentPromise = p;
  return p;
}

export async function playB64(b64, mime = "audio/mpeg") {
  if (!b64) return;
  const url = b64ToBlobUrl(b64, mime);
  if (!url) return;
  try {
    await playUrl(url);
  } finally {
    // One-shot blob URLs (uncached): revoke so we don't leak.
    if (![..._blobUrlCache.values()].includes(url)) {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    }
  }
}

/** Stop main playback immediately. */
export function stop() {
  _resetMain();
}

export function pause() {
  try { _main?.pause(); } catch { /* ignore */ }
  try { _chime?.pause(); } catch { /* ignore */ }
}

export function resume() {
  try {
    const el = _main;
    if (el && el.paused && el.src) el.play().catch(() => {});
  } catch { /* ignore */ }
}

/**
 * Play a short overlay sound (correct/wrong feedback ding) without
 * interrupting the main word playback. Resolves on `ended`.
 */
export function playChime(url) {
  return new Promise((resolve) => {
    if (!url) return resolve();
    const el = getChime();
    try {
      el.pause();
      el.currentTime = 0;
    } catch { /* ignore */ }
    const cleanup = () => {
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onErr);
    };
    const onEnd = () => { cleanup(); resolve(); };
    const onErr = (e) => {
      cleanup();
      console.warn("[audio] chime error:", e?.error || e?.message || e?.name || e);
      resolve();
    };
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onErr);
    try {
      el.src = url;
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          // Don't block UX on chime failures — just log.
          console.warn("[audio] chime play() rejected:", err?.name || err);
          cleanup();
          resolve();
        });
      }
    } catch (e) { onErr(e); }
  });
}

if (typeof window !== "undefined") {
  // Safety net: surface a one-shot unlock on the next user gesture even
  // if no specific route called us. Most pages call unlockAudio()
  // explicitly when the user taps a Start/Practice button.
  const handler = () => {
    unlockAudio().finally(() => {
      window.removeEventListener("click", handler);
      window.removeEventListener("touchend", handler);
      window.removeEventListener("keydown", handler);
    });
  };
  window.addEventListener("click", handler, { passive: true });
  window.addEventListener("touchend", handler, { passive: true });
  window.addEventListener("keydown", handler, { passive: true });
  if (isIOS) console.debug("[audio] iOS detected — Silent Mode bypass active");
}
